import React, { useEffect, useContext, useState } from "react"
import PropTypes from "prop-types"
import { AppContext } from "../components/context/AppContext"
import { OfferApi } from "../utils/api"
import { failOpenMaintenanceMode } from "../utils/maintenanceFlag"
import { MaintenanceModeComponent, PageNotFound, CheckStatusComponent } from "../components"
import { CostCoGetStartedComponent, CostCoWelcomeComponent } from "../components/CostCo"
import AnalyticsService from "../utils/AnalyticsService"
import { ROUTE_PATHS } from "../utils/routes"

const offerId = Number.parseInt(process.env.REACT_APP_COSTCO_OFFER_ID)

export const CostCo = () => {
  const { dispatch, state } = useContext(AppContext)
  const [inMaintenanceMode, setInMaintenanceMode] = useState(true)
  const [maintenanceText, setMaintenanceText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isErrorFetchingMaintenanceText, setIsErrorFetchingMaintenanceText] = useState(false)
  const [landingPageImageText, setLandingPageImageText] = useState("")
  const [landingPageImage, setLandingPageImage] = useState("")
  const [landingPageFirstSection, setLandingPageFirstSection] = useState("")
  const [landingPageSecSection, setLandingPageSecSection] = useState("")
  const [campaignId, setCampaignId] = useState(0)
  const [isPageNotFound, setIsPageNotFound] = useState(false)
  const [offerEpire, setOfferExpire] = useState("")

  useEffect(() => {
    AnalyticsService.sendUniquePageview(ROUTE_PATHS.SAMSCLUB)
    dispatch({ type: "RESETUSER" })
  }, [dispatch])

  // Fetch all data for CostCo page
  const fetchConfigCostcoData = async (isMounted) => {
    setIsLoading(true)
    setErrorMessage("")
    setIsErrorFetchingMaintenanceText(false)
    try {
      const [clientFlag, clientText, pageNotFound, offerFlag, offerText] = await Promise.all([
        OfferApi.getClientAsset("MaintenanceFlag"),
        OfferApi.getClientAsset("MaintenanceText"),
        OfferApi.getOfferAsset(offerId, "PAGE_NOT_FOUND"),
        OfferApi.getOfferAsset(offerId, "MaintenanceFlag"),
        OfferApi.getOfferAsset(offerId, "MaintenanceText")
      ])
      if (!isMounted) { return }
      setIsPageNotFound(pageNotFound)
      const clientMaint = clientFlag.notes?.toLowerCase() === "true"
      const offerMaint = offerFlag.notes?.toLowerCase() === "true"
      if (clientMaint || offerMaint) {
        setMaintenanceText(clientMaint ? clientText.notes : offerText.notes)
        setInMaintenanceMode(true)
        return
      }
      setInMaintenanceMode(false)
      const [mainImage, mainImageText, firstSectionText, secondSectionText] = await Promise.all([
        OfferApi.getOfferAsset(offerId, "BANNER_IMAGE_LANDING_PAGE"),
        OfferApi.getOfferAsset(offerId, "IMAGE_TEXT_LANDING_PAGE"),
        OfferApi.getClientAsset("COSTCO_OFFER_TEXT_1"),
        OfferApi.getClientAsset("COSTCO_OFFER_TEXT_2")
      ])
      if (!isMounted) { return }
      setLandingPageImage(mainImage?.notes || "")
      setLandingPageImageText(mainImageText?.notes || "")
      setLandingPageFirstSection(firstSectionText?.notes || "")
      setLandingPageSecSection(secondSectionText?.notes || "")
    } catch (error) {
      if (isMounted) {
        failOpenMaintenanceMode(setInMaintenanceMode, setIsErrorFetchingMaintenanceText)
      }
    } finally {
      if (isMounted) { setIsLoading(false) }
    }
  }

  useEffect(() => {
    let isMounted = true
    fetchConfigCostcoData(isMounted)
    return () => { isMounted = false }
  }, [])

  useEffect(() => {
    const getOfferCampignForImage = async () => {
      setIsLoading(true)
      try {
        const resOfferCampaignDetails = await OfferApi.getOfferCampaignDetails(offerId)
        resOfferCampaignDetails.data.campaignId === 0 ? setOfferExpire("OFFER_EXPIRED") : setCampaignId(resOfferCampaignDetails.data.campaignId)
        setIsLoading(false)
      } catch (error) {
        if (error.message === "Unable to retrieve offer campaign details") {
          setOfferExpire("OFFER_EXPIRED")
        }
      } finally {
        setIsLoading(false)
      }
    }
    getOfferCampignForImage()
  }, [])


  const ActiveContent = () => (
    <>
      {!isPageNotFound && (
        <CostCoGetStartedComponent
          errorMessage={state.commonError}
          offerId={offerId}
          firstSectionText={landingPageFirstSection}
          secondSectionText={landingPageSecSection}
          setErrorMessage={setErrorMessage}
        />
      )}
      <CheckStatusComponent />
    </>
  )

  return (
    <>
      <CostCoWelcomeComponent backgrndImgText={landingPageImageText} backgrndImg={landingPageImage} offerCampaignId={campaignId} offerCampaignIdExpire={offerEpire} />
      {isPageNotFound && <PageNotFound />}
      {isErrorFetchingMaintenanceText && (
        <div className="text-center container">
          <h1 className="magenta">Temporarily Unavailable</h1>
          <p>This site is currently under going scheduled maintenance. We&apos;ll be back soon.</p>
          <p>Thank you for your patience.</p>
        </div>
      )}
      {isLoading && <div style={{ textAlign: "center" }}>Loading ...</div>}
      {errorMessage && !isLoading && !isErrorFetchingMaintenanceText && (
        <div style={{ textAlign: "center" }}>{errorMessage}</div>
      )}
      {inMaintenanceMode && !isLoading && <MaintenanceModeComponent maintenanceText={maintenanceText} />}
      {!inMaintenanceMode && !isLoading && !isErrorFetchingMaintenanceText && <ActiveContent />}
    </>
  )
}

CostCo.propTypes = {
  history: PropTypes.object
}
